<template>
    <modal
        v-if="show"
        class="complianceModal"
        :title="'Purchase Trusted Calling'"
        :form="form"
        :submitText="submitTextModal"
        :cancelText="'Cancel'"
        :backText="'Previous'"
        :submitClasses="{
            'is-primary': true,
            'is-busy': form.busy,
        }"
        :actionLeft="actionLeft"
        :submitDisabled="submitDisabled"
        :hideBack="!showBackButton"
        :hideCancel="!showCancelButton"
        @back="handlePreviousStep"
        @closed="handleClosed"
        @submit="handleNextStep"
    >
        <configuration-stepper :type="'simple'" :currentStep="currentStep" :steps="steps" />
        <div v-if="currentStep === 0" class="complianceModal__content complianceModal__preview">
            <div class="complianceModal__previewTitle">Voice Integrity</div>

            <div class="complianceModal__previewDescription">
                Remediate spam labels on your phone numbers with <br />
                Voice Integrity.
            </div>

            <div class="complianceModal__previewImage">
                <img :src="`${$window.Config.cdn_url}/images/voice-integrity.png`" alt="" />
            </div>
        </div>

        <div v-if="currentStep === 1" class="complianceModal__content complianceModal__preview">
            <div class="complianceModal__previewTitle">STIR/SHAKEN</div>

            <div class="complianceModal__previewDescription">
                Increase trust and answer rates in your calls by <br />
                displaying "Caller Verified."
            </div>

            <div class="complianceModal__previewImage">
                <img :src="`${$window.Config.cdn_url}/images/stir-shaken.png`" alt="" />
            </div>
        </div>

        <div v-if="currentStep === 2" class="complianceModal__content complianceModal__preview">
            <div class="complianceModal__previewTitle">CNAM | Display your business name</div>

            <div class="complianceModal__previewDescription">
                Brand your outgoing calls with your company name using <br />
                our verified Caller ID (CNAM) service.
            </div>

            <div class="complianceModal__previewImage">
                <img :src="`${$window.Config.cdn_url}/images/cnam.png`" alt="" />
            </div>
        </div>

        <div v-if="currentStep === 3" class="complianceModal__content complianceModal--buyStep2">
            <article v-if="errorMessage" class="message is-warning">
                <div class="message-body">
                    <div class="message-content">
                        <fa-icon icon="exclamation-triangle" />
                        <span>
                            {{ errorMessage }}
                        </span>
                    </div>
                </div>
            </article>

            <div class="complianceModal__title">Purchase Trusted Calling Add-on</div>

            <div class="complianceModal__column">
                <p>Would you like to add Trusted Calling for just {{ price }}?</p>
                <plan-tax-line v-if="taxLine" :prorate="taxLine" paddingless />
            </div>
        </div>
    </modal>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import PlanTaxLine from 'components/Billing/components/PlanTaxLine.vue';
import Form from 'core/Form';
import filters from '../../../../../../vue/filters';

export default {
    components: {
        PlanTaxLine,
    },

    emits: ['handleShowShakenStirAdvanced', 'handleShowCNAMCallerID', 'closed'],

    data() {
        return {
            form: new Form({}),
            currentStep: 0,
            steps: 3,
            taxLine: null,
            showBackButton: false,
            showCancelButton: true,
            actionLeft: true,
            errorMessage: '',
        };
    },

    computed: {
        type() {
            return this.typeTrustedCallingBuyModal;
        },

        show() {
            return this.showTrustedCallingBuyModal;
        },

        price() {
            if (this.complianceBillingInfo.interval === 'yearly') {
                return `${filters.currency(this.complianceBillingInfo.price, 2)}/annual`;
            }

            if (this.complianceBillingInfo.interval === 'quarterly') {
                return `${filters.currency(this.complianceBillingInfo.price, 2)}/3 months`;
            }

            if (this.complianceBillingInfo.interval === 'semi-annual') {
                return `${filters.currency(this.complianceBillingInfo.price, 2)}/6 months`;
            }

            return `${filters.currency(this.complianceBillingInfo.price, 2)}/month`;
        },

        submitTextModal() {
            return this.currentStep === this.steps ? 'Buy Now' : 'Next';
        },

        submitDisabled() {
            return false;
        },

        ...mapState(stores.CallingStore, [
            'typeTrustedCallingBuyModal',
            'showTrustedCallingBuyModal',
        ]),
        ...mapState(stores.ComplianceProfile, ['complianceBillingInfo']),
        ...mapState(stores.Subscriptions, ['subscription']),
    },

    watch: {
        show(value) {
            if (value) {
                this.init();
            }
        },
    },

    methods: {
        init() {
            this.reset();
            this.calculateButtons();
            this.loadTaxLine();
        },

        async loadTaxLine() {
            const planId = this.subscription?.plan?.compliance_plan_id;

            if (!planId) return;

            try {
                const data = await this.$store.dispatch('billingCompliancePreview', planId);
                this.taxLine = data;
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'loadTaxLine');
            }
        },

        reset() {
            this.form.reset();
            this.currentStep = 0;
            this.showBackButton = true;
            this.showCancelButton = false;
            this.actionLeft = true;
            this.errorMessage = '';
        },

        setStep(step) {
            this.currentStep = step;
            this.calculateButtons();
        },

        calculateButtons() {
            if (this.currentStep > 0) {
                this.showBackButton = true;
                this.showCancelButton = false;
            } else {
                this.showBackButton = false;
                this.showCancelButton = true;
            }
        },

        async handleNextStep() {
            if (this.currentStep === 0) {
                this.setStep(1);

                return;
            }

            if (this.currentStep === 1) {
                this.setStep(2);

                return;
            }

            if (this.currentStep === 2) {
                this.setStep(3);

                return;
            }

            if (this.currentStep === 3) {
                await this.handleSubmit();
            }
        },

        handlePreviousStep() {
            if (this.currentStep === 1) {
                this.setStep(0);
            }

            if (this.currentStep === 2) {
                this.setStep(1);
            }

            if (this.currentStep === 3) {
                this.setStep(2);
            }
        },

        async handleSubmit() {
            try {
                this.form.busy = true;
                this.errorMessage = '';

                await this.$store.dispatch('billingComplianceUpdate');

                if (this.complianceBillingInfo.subscribed) {
                    if (this.type === 'shaken-stir') {
                        this.$emit('handleShowShakenStirAdvanced', true);
                    }

                    if (this.type === 'cnam') {
                        this.$emit('handleShowCNAMCallerID', true);
                    }

                    this.handleClosed();
                } else {
                    this.errorMessage = 'Your card was declined';
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleSubmit');

                this.errorMessage = 'Your card was declined';
            } finally {
                this.form.busy = false;
            }
        },

        handleClosed() {
            this.$store.dispatch('handleShowTrustedCallingBuy', {
                status: false,
                type: null,
            });
            this.$emit('closed');
        },
    },
};
</script>
